import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { showAlertForError } from '@/mastodon/actions/alerts';
import { importFetchedAccounts } from '@/mastodon/actions/importer';
import {
  apiGetChatRooms,
  apiGetChatRoom,
  apiCreateChatRoom,
  apiRenameChatRoom,
  apiLeaveChatRoom,
  apiMarkChatRead,
  apiAcceptChatRoom,
  apiSetChatRoomEncryption,
  apiConfirmChatKey,
  apiGetChatMessages,
  apiSendChatMessage,
  apiDeleteChatMessage,
  apiAddChatParticipants,
  apiRemoveChatParticipant,
} from '@/mastodon/api/chat';
import type {
  ApiChatRoomJSON,
  ApiChatMessageJSON,
  ApiCreateChatPayload,
  ApiSendChatMessagePayload,
  ApiChatEncryptionPayload,
} from '@/mastodon/api_types/chat';
import { browserHistory } from '@/mastodon/components/router';
import { me } from '@/mastodon/initial_state';
import { createAppAsyncThunk } from '@/mastodon/store/typed_functions';

export const CHAT_ROOMS_PAGE_SIZE = 40;
export const CHAT_MESSAGES_PAGE_SIZE = 40;

interface ChatCollectionState {
  hasMore: boolean;
  isLoading: boolean;
  initialized: boolean;
}

interface ChatMessagesState extends ChatCollectionState {
  ids: string[]; // ascending: oldest -> newest
}

interface ChatState {
  rooms: Record<string, ApiChatRoomJSON>;
  roomOrder: string[];
  roomList: ChatCollectionState;
  messages: Record<string, ApiChatMessageJSON>;
  messagesByRoom: Record<string, ChatMessagesState>;
  drafts: Record<string, string>;
  activeRoomId: string | null;
}

const initialState: ChatState = {
  rooms: {},
  roomOrder: [],
  roomList: { hasMore: false, isLoading: false, initialized: false },
  messages: {},
  messagesByRoom: {},
  drafts: {},
  activeRoomId: null,
};

// Snowflake ids: numeric strings. Longer string == larger number; same length compares lexically.
const compareIds = (a: string, b: string) => {
  if (a.length === b.length) return a < b ? -1 : a > b ? 1 : 0;
  return a.length < b.length ? -1 : 1;
};

const roomActivityId = (room: ApiChatRoomJSON) =>
  room.last_message?.id ?? room.id;

const sortRoomOrder = (state: ChatState) => {
  state.roomOrder.sort((a, b) => {
    const roomA = state.rooms[a];
    const roomB = state.rooms[b];
    if (!roomA || !roomB) return 0;
    return -compareIds(roomActivityId(roomA), roomActivityId(roomB));
  });
};

const emptyMessages = (): ChatMessagesState => ({
  ids: [],
  hasMore: false,
  isLoading: false,
  initialized: false,
});

const upsertRoom = (state: ChatState, room: ApiChatRoomJSON) => {
  state.rooms[room.id] = room;
  if (!state.roomOrder.includes(room.id)) state.roomOrder.push(room.id);
  sortRoomOrder(state);
};

const dropRoom = (state: ChatState, roomId: string) => {
  /* eslint-disable @typescript-eslint/no-dynamic-delete */
  delete state.rooms[roomId];
  delete state.messagesByRoom[roomId];
  delete state.drafts[roomId];
  /* eslint-enable @typescript-eslint/no-dynamic-delete */
  state.roomOrder = state.roomOrder.filter((id) => id !== roomId);
  if (state.activeRoomId === roomId) state.activeRoomId = null;
};

const importMessage = (state: ChatState, message: ApiChatMessageJSON) => {
  state.messages[message.id] = message;

  const roomState = (state.messagesByRoom[message.chat_room_id] ??=
    emptyMessages());

  if (!roomState.ids.includes(message.id)) {
    roomState.ids.push(message.id);
    roomState.ids.sort(compareIds);
  }
};

const applyMessageToRoom = (state: ChatState, message: ApiChatMessageJSON) => {
  importMessage(state, message);

  const room = state.rooms[message.chat_room_id];
  if (!room) return;

  if (!room.last_message || compareIds(message.id, room.last_message.id) > 0) {
    room.last_message = message;
    room.updated_at = message.created_at;
  }

  sortRoomOrder(state);

  const isMine = !!me && message.account.id === me;
  if (!isMine && state.activeRoomId !== message.chat_room_id) {
    room.unread_count += 1;
  }
};

export const fetchChatRooms = createAppAsyncThunk(
  'chat/fetchRooms',
  async ({ maxId }: { maxId?: string }, { dispatch }) => {
    const rooms = await apiGetChatRooms(maxId ? { max_id: maxId } : undefined);
    dispatch(importFetchedAccounts(rooms.flatMap((room) => room.accounts)));
    return { rooms, append: !!maxId };
  },
);

export const fetchChatRoom = createAppAsyncThunk(
  'chat/fetchRoom',
  async (id: string, { dispatch }) => {
    const room = await apiGetChatRoom(id);
    dispatch(importFetchedAccounts(room.accounts));
    return room;
  },
);

export const createChatRoom = createAppAsyncThunk(
  'chat/createRoom',
  async (payload: ApiCreateChatPayload, { dispatch }) => {
    try {
      const room = await apiCreateChatRoom(payload);
      dispatch(importFetchedAccounts(room.accounts));
      return room;
    } catch (error) {
      dispatch(showAlertForError(error));
      throw error;
    }
  },
);

// Open (creating if needed) the 1:1 room with a single account and navigate to it.
export const startDirectChat = createAppAsyncThunk(
  'chat/startDirect',
  async (accountId: string, { dispatch }) => {
    try {
      const room = await apiCreateChatRoom({ account_ids: [accountId] });
      dispatch(importFetchedAccounts(room.accounts));
      browserHistory.push(`/chat/${room.id}`);
      return room;
    } catch (error) {
      dispatch(showAlertForError(error));
      throw error;
    }
  },
);

export const renameChatRoom = createAppAsyncThunk(
  'chat/renameRoom',
  ({ id, name }: { id: string; name: string }) => apiRenameChatRoom(id, name),
);

export const leaveChatRoom = createAppAsyncThunk(
  'chat/leaveRoom',
  async (id: string) => {
    await apiLeaveChatRoom(id);
    return id;
  },
);

export const acceptChatRoom = createAppAsyncThunk(
  'chat/acceptRoom',
  (id: string) => apiAcceptChatRoom(id),
);

export const setChatRoomEncryption = createAppAsyncThunk(
  'chat/setEncryption',
  ({ id, ...payload }: { id: string } & ApiChatEncryptionPayload) =>
    apiSetChatRoomEncryption(id, payload),
);

export const confirmChatKey = createAppAsyncThunk(
  'chat/confirmKey',
  (id: string) => apiConfirmChatKey(id),
);

export const fetchChatMessages = createAppAsyncThunk(
  'chat/fetchMessages',
  async (
    { roomId, maxId }: { roomId: string; maxId?: string },
    { dispatch },
  ) => {
    const messages = await apiGetChatMessages(
      roomId,
      maxId ? { max_id: maxId } : undefined,
    );
    dispatch(importFetchedAccounts(messages.map((message) => message.account)));
    return { roomId, messages, append: !!maxId };
  },
);

export const sendChatMessage = createAppAsyncThunk(
  'chat/sendMessage',
  async (
    { roomId, ...payload }: { roomId: string } & ApiSendChatMessagePayload,
    { dispatch },
  ) => {
    try {
      const message = await apiSendChatMessage(roomId, payload);
      return message;
    } catch (error) {
      // Without this, a failed send (a request-limit rejection, a network
      // blip, anything) is completely silent on the sender's end -- no
      // error, but also no message, and nothing to tell them it didn't
      // go through. The recipient, meanwhile, already has a visible
      // (now permanently empty) room: creating the room and sending the
      // first message are two separate requests, and room creation
      // already fans out to both sides on its own (see
      // Api::V1::ChatsController#create's ChatDelivery.room_updated)
      // before either of them have exchanged a single message.
      dispatch(showAlertForError(error));
      throw error;
    }
  },
);

export const deleteChatMessage = createAppAsyncThunk(
  'chat/deleteMessage',
  async ({ roomId, id }: { roomId: string; id: string }) => {
    await apiDeleteChatMessage(roomId, id);
    return { roomId, id };
  },
);

export const markChatRead = createAppAsyncThunk(
  'chat/markRead',
  ({ roomId, maxId }: { roomId: string; maxId?: string }) =>
    apiMarkChatRead(roomId, maxId),
);

export const addChatParticipants = createAppAsyncThunk(
  'chat/addParticipants',
  async (
    { roomId, accountIds }: { roomId: string; accountIds: string[] },
    { dispatch },
  ) => {
    const room = await apiAddChatParticipants(roomId, accountIds);
    dispatch(importFetchedAccounts(room.accounts));
    return room;
  },
);

export const removeChatParticipant = createAppAsyncThunk(
  'chat/removeParticipant',
  ({ roomId, accountId }: { roomId: string; accountId: string }) =>
    apiRemoveChatParticipant(roomId, accountId),
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    chatMessageReceived(state, action: PayloadAction<ApiChatMessageJSON>) {
      applyMessageToRoom(state, action.payload);
    },
    chatRoomReceived(state, action: PayloadAction<ApiChatRoomJSON>) {
      upsertRoom(state, action.payload);
    },
    chatRoomDeleted(state, action: PayloadAction<{ id: string }>) {
      dropRoom(state, action.payload.id);
    },
    chatMessageDeleted(
      state,
      action: PayloadAction<{ id: string; chat_room_id: string }>,
    ) {
      const message = state.messages[action.payload.id];
      if (message) {
        message.deleted = true;
        message.content = '';
        message.media_attachments = [];
      }
    },
    chatReadReceived(
      state,
      action: PayloadAction<{
        chat_room_id: string;
        account_id: string;
        last_read_message_id: string | null;
      }>,
    ) {
      const room = state.rooms[action.payload.chat_room_id];
      if (!room) return;

      const existing = room.read_states.find(
        (entry) => entry.account_id === action.payload.account_id,
      );
      if (existing) {
        existing.last_read_message_id = action.payload.last_read_message_id;
      } else {
        room.read_states.push({
          account_id: action.payload.account_id,
          last_read_message_id: action.payload.last_read_message_id,
        });
      }
    },
    setActiveRoom(state, action: PayloadAction<string | null>) {
      state.activeRoomId = action.payload;
      if (action.payload) {
        const room = state.rooms[action.payload];
        if (room) room.unread_count = 0;
      }
    },
    setRoomDraft(
      state,
      action: PayloadAction<{ roomId: string; text: string }>,
    ) {
      state.drafts[action.payload.roomId] = action.payload.text;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchChatRooms.pending, (state) => {
        state.roomList.isLoading = true;
      })
      .addCase(fetchChatRooms.rejected, (state) => {
        state.roomList.isLoading = false;
      })
      .addCase(fetchChatRooms.fulfilled, (state, action) => {
        const { rooms, append } = action.payload;
        if (!append) state.roomOrder = [];
        rooms.forEach((room) => {
          state.rooms[room.id] = room;
          if (!state.roomOrder.includes(room.id)) state.roomOrder.push(room.id);
        });
        sortRoomOrder(state);
        state.roomList.isLoading = false;
        state.roomList.initialized = true;
        state.roomList.hasMore = rooms.length >= CHAT_ROOMS_PAGE_SIZE;
      })
      .addCase(fetchChatRoom.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(createChatRoom.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(startDirectChat.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(renameChatRoom.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(addChatParticipants.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(removeChatParticipant.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(markChatRead.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(acceptChatRoom.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(setChatRoomEncryption.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(confirmChatKey.fulfilled, (state, action) => {
        upsertRoom(state, action.payload);
      })
      .addCase(leaveChatRoom.fulfilled, (state, action) => {
        dropRoom(state, action.payload);
      })
      .addCase(fetchChatMessages.pending, (state, action) => {
        (state.messagesByRoom[action.meta.arg.roomId] ??=
          emptyMessages()).isLoading = true;
      })
      .addCase(fetchChatMessages.rejected, (state, action) => {
        const roomState = state.messagesByRoom[action.meta.arg.roomId];
        if (roomState) roomState.isLoading = false;
      })
      .addCase(fetchChatMessages.fulfilled, (state, action) => {
        const { roomId, messages, append } = action.payload;
        const roomState = (state.messagesByRoom[roomId] ??= emptyMessages());

        messages.forEach((message) => {
          state.messages[message.id] = message;
        });

        const merged = new Set(append ? roomState.ids : []);
        if (!append) {
          // Initial load: keep any live-streamed ids we already had.
          roomState.ids.forEach((id) => merged.add(id));
        }
        messages.forEach((message) => merged.add(message.id));

        roomState.ids = Array.from(merged).sort(compareIds);
        roomState.isLoading = false;
        roomState.initialized = true;
        roomState.hasMore = messages.length >= CHAT_MESSAGES_PAGE_SIZE;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        applyMessageToRoom(state, action.payload);
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete state.drafts[action.payload.chat_room_id];
      })
      .addCase(deleteChatMessage.fulfilled, (state, action) => {
        const message = state.messages[action.payload.id];
        if (message) {
          message.deleted = true;
          message.content = '';
          message.media_attachments = [];
        }
      });
  },
});

export const {
  chatMessageReceived,
  chatRoomReceived,
  chatRoomDeleted,
  chatMessageDeleted,
  chatReadReceived,
  setActiveRoom,
  setRoomDraft,
} = chatSlice.actions;

export const chat = chatSlice.reducer;
